import { type SupplierCertificateType, QA_SKIP_CERTS } from '@tandemone/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ShieldCheck, Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'

import { extractErrorMessage } from '@/api/extract-error'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  type UploadedDocument,
  deleteDocument,
  FileDropZone,
  getDocument,
  useDocumentUpload
} from '@/domains/document'
import { captureException } from '@/domains/observability'
import { navigateTab, openBlankTab } from '@/lib/new-tab'

import { getSupplierCertificates, replaceSupplierCertificates } from './api'
import { QA_SKIP_CERTS_LABEL, SUPPLIER_CERTIFICATE_LABELS } from './enum-labels'
import { FormSkeleton } from './form-skeleton'
import { invalidateOnboarding } from './invalidate-onboarding'

const CERT_MAX_BYTES = 50 * 1024 * 1024

type CertEntry = {
  selected: boolean
  documentId: string | null
  fileName: string | null
  contentType: string | null
  createdAt: string | null
}

type CertState = Record<SupplierCertificateType, CertEntry>

const ALL_CERTS = Object.keys(
  SUPPLIER_CERTIFICATE_LABELS
) as SupplierCertificateType[]

function buildInitial(
  rows: ReadonlyArray<{
    certification: SupplierCertificateType
    documentId: string | null
    documentFileName: string | null
    documentContentType: string | null
    createdAt: string
  }>
): CertState {
  const state = {} as CertState
  for (const cert of ALL_CERTS) {
    state[cert] = {
      selected: false,
      documentId: null,
      fileName: null,
      contentType: null,
      createdAt: null
    }
  }
  for (const row of rows) {
    state[row.certification] = {
      selected: true,
      documentId: row.documentId,
      fileName: row.documentFileName,
      contentType: row.documentContentType,
      createdAt: row.createdAt
    }
  }
  return state
}

export function SupplierCertificatesForm() {
  const queryClient = useQueryClient()
  const [state, setState] = useState<CertState | null>(null)
  const [selectionOrder, setSelectionOrder] = useState<
    SupplierCertificateType[]
  >([])
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [pendingRemoval, setPendingRemoval] =
    useState<SupplierCertificateType | null>(null)

  const { data } = useQuery({
    queryKey: ['supplier-certificates'],
    queryFn: ({ signal }) => getSupplierCertificates(signal)
  })

  const autoSaveMutation = useMutation({
    mutationFn: replaceSupplierCertificates,
    onSuccess: async () => {
      await invalidateOnboarding(queryClient, 'supplier-certificates')
    },
    onError: error => {
      captureException(error)
      setSubmitError(extractErrorMessage(error))
    }
  })

  if (!data) return <FormSkeleton rows={5} />

  const certificates = data.certificates
  const initial = buildInitial(certificates)
  const current = state ?? initial

  /**
   * selectionOrder is newest-first for the in-session adds. persisted
   * certs hydrate at the bottom in their existing order so the user can
   * always see fresh adds at top.
   */
  const persistedOrder = certificates
    .map(row => row.certification)
    .filter(cert => current[cert].selected)
    .filter(cert => !selectionOrder.includes(cert))
  const selectedCerts: SupplierCertificateType[] = [
    ...selectionOrder.filter(cert => current[cert].selected),
    ...persistedOrder
  ]
  const availableCerts = ALL_CERTS.filter(cert => !current[cert].selected)
  const hasVerifiedSkipCert = selectedCerts.some(
    cert => QA_SKIP_CERTS.includes(cert) && current[cert].documentId !== null
  )

  function setEntry(cert: SupplierCertificateType, entry: CertEntry) {
    setSubmitError(null)
    setState(prev => {
      const base = prev ?? initial
      return { ...base, [cert]: entry }
    })
  }

  function buildPayload(nextState: CertState) {
    return ALL_CERTS.filter(
      cert => nextState[cert].selected && nextState[cert].documentId !== null
    ).map(cert => ({
      certification: cert,
      documentId: nextState[cert].documentId!
    }))
  }

  /**
   * persist immediately when a verified state lands. without this, a user
   * who uploads a cert and navigates away loses the link (the document row
   * lives, but supplier_certificate is never written). save and continue
   * stays as the explicit commit path.
   */
  function autoSave(nextState: CertState) {
    autoSaveMutation.mutate({ certificates: buildPayload(nextState) })
  }

  function add(cert: SupplierCertificateType) {
    setEntry(cert, {
      selected: true,
      documentId: null,
      fileName: null,
      contentType: null,
      createdAt: null
    })
    setSelectionOrder(prev => (prev.includes(cert) ? prev : [cert, ...prev]))
  }

  function handleUploaded(
    cert: SupplierCertificateType,
    uploaded: UploadedDocument
  ) {
    const previousDocumentId = current[cert].documentId
    const base = state ?? initial
    const nextState: CertState = {
      ...base,
      [cert]: {
        selected: true,
        documentId: uploaded.documentId,
        fileName: uploaded.fileName,
        contentType: uploaded.contentType,
        /**
         * placeholder timestamp. the next refetch (triggered by autoSave's
         * onSuccess invalidate) overwrites this with the server's value
         */
        createdAt: new Date().toISOString()
      }
    }
    setState(nextState)
    setSubmitError(null)
    if (previousDocumentId && previousDocumentId !== uploaded.documentId) {
      void deleteDocument(previousDocumentId).catch(() => {
        // best-effort cleanup, new doc is already linked
      })
    }
    autoSave(nextState)
  }

  function remove(cert: SupplierCertificateType) {
    const existing = current[cert]
    const base = state ?? initial
    const nextState: CertState = {
      ...base,
      [cert]: {
        selected: false,
        documentId: null,
        fileName: null,
        contentType: null,
        createdAt: null
      }
    }
    setState(nextState)
    setSubmitError(null)
    setSelectionOrder(prev => prev.filter(item => item !== cert))
    if (existing.documentId) {
      void deleteDocument(existing.documentId).catch(() => {
        // best-effort cleanup, UI proceeds either way
      })
    }
    autoSave(nextState)
  }

  async function viewDocument(documentId: string, tab: Window | null) {
    try {
      const result = await getDocument(documentId)
      navigateTab(result.previewUrl, tab)
    } catch (error) {
      tab?.close()
      if (error instanceof Error) captureException(error)
      setSubmitError(extractErrorMessage(error))
    }
  }

  function confirmRemoval() {
    if (!pendingRemoval) return
    remove(pendingRemoval)
    setPendingRemoval(null)
  }

  const removalIsLastVerifiedSkip = (() => {
    if (!pendingRemoval) return false
    if (!QA_SKIP_CERTS.includes(pendingRemoval)) return false
    if (current[pendingRemoval].documentId === null) return false
    const otherVerifiedSkips = selectedCerts.filter(
      cert =>
        cert !== pendingRemoval &&
        QA_SKIP_CERTS.includes(cert) &&
        current[cert].documentId !== null
    )
    return otherVerifiedSkips.length === 0
  })()

  return (
    <div className='flex w-full flex-col gap-4'>
      {hasVerifiedSkipCert && <QaSkipBanner />}

      {(() => {
        const pending = selectedCerts.filter(
          c => current[c].documentId === null
        )
        const verified = selectedCerts.filter(
          c => current[c].documentId !== null
        )
        return (
          <>
            {pending.length > 0 && (
              <section className='flex flex-col gap-2 rounded-2xl border border-border bg-page p-6'>
                {pending.map(cert => (
                  <FileDropZone
                    key={cert}
                    accept='application/pdf,image/png,image/jpeg'
                    maxBytes={CERT_MAX_BYTES}
                    label={SUPPLIER_CERTIFICATE_LABELS[cert]}
                    value={null}
                    onUploaded={(uploaded: UploadedDocument) =>
                      handleUploaded(cert, uploaded)
                    }
                    onRemove={() => setPendingRemoval(cert)}
                  />
                ))}
              </section>
            )}
            {verified.length > 0 && (
              <section className='rounded-2xl border border-border bg-page p-2'>
                <div className='flex flex-col gap-2 sm:hidden'>
                  {verified.map(cert => {
                    const entry = current[cert]
                    return (
                      <CertRow
                        key={cert}
                        variant='card'
                        cert={cert}
                        fileName={entry.fileName}
                        contentType={entry.contentType}
                        createdAt={entry.createdAt}
                        onView={() =>
                          void viewDocument(entry.documentId!, openBlankTab())
                        }
                        onUploaded={uploaded => handleUploaded(cert, uploaded)}
                        onRemove={() => setPendingRemoval(cert)}
                      />
                    )
                  })}
                </div>
                <Table className='hidden sm:table'>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Certificate</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Added</TableHead>
                      <TableHead className='w-24 text-right'>Replace</TableHead>
                      <TableHead className='w-12 text-right' />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {verified.map(cert => {
                      const entry = current[cert]
                      return (
                        <CertRow
                          key={cert}
                          cert={cert}
                          fileName={entry.fileName}
                          contentType={entry.contentType}
                          createdAt={entry.createdAt}
                          onView={() =>
                            void viewDocument(entry.documentId!, openBlankTab())
                          }
                          onUploaded={uploaded =>
                            handleUploaded(cert, uploaded)
                          }
                          onRemove={() => setPendingRemoval(cert)}
                        />
                      )
                    })}
                  </TableBody>
                </Table>
              </section>
            )}
          </>
        )
      })()}

      {availableCerts.length > 0 && (
        <section className='flex flex-col gap-2 rounded-2xl border border-border bg-page p-6'>
          <p className='text-[12px] font-medium text-ink-muted'>
            Add a certification
          </p>
          <div className='grid gap-2 sm:grid-cols-2'>
            {availableCerts.map(cert => (
              <button
                key={cert}
                type='button'
                onClick={() => add(cert)}
                className='cursor-pointer rounded-xl border border-border bg-page px-4 py-3 text-left text-[14px] font-medium text-ink transition-colors hover:border-accent/40 hover:bg-cream/40'
              >
                + {SUPPLIER_CERTIFICATE_LABELS[cert]}
              </button>
            ))}
          </div>
        </section>
      )}

      {submitError && <p className='text-sm text-red-600'>{submitError}</p>}

      <Dialog
        open={pendingRemoval !== null}
        onOpenChange={open => {
          if (!open) setPendingRemoval(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Remove{' '}
              {pendingRemoval
                ? SUPPLIER_CERTIFICATE_LABELS[pendingRemoval]
                : 'certificate'}
              ?
            </DialogTitle>
            <DialogDescription>
              {removalIsLastVerifiedSkip
                ? 'This cert auto-completes the quality assessment. Removing it means you’ll need to fill out the 29-question assessment.'
                : 'This will be removed from your certifications. The uploaded file stays in your audit history.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type='button'
              onClick={() => setPendingRemoval(null)}
              className='cursor-pointer rounded-md border border-border bg-page px-3 py-1.5 text-[12px] font-medium text-ink-soft hover:bg-cream/40 hover:text-ink'
            >
              Cancel
            </button>
            <button
              type='button'
              onClick={confirmRemoval}
              className='cursor-pointer rounded-md bg-destructive px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-[#7f1d1d]'
            >
              Remove
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const CERT_ACCEPT = 'application/pdf,image/png,image/jpeg'

function CertRow({
  cert,
  fileName,
  contentType,
  createdAt,
  variant = 'row',
  onView,
  onUploaded,
  onRemove
}: {
  cert: SupplierCertificateType
  fileName: string | null
  contentType: string | null
  createdAt: string | null
  variant?: 'row' | 'card'
  onView: () => void
  onUploaded: (uploaded: UploadedDocument) => void
  onRemove: () => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const upload = useDocumentUpload()

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (file.size > CERT_MAX_BYTES) return
    try {
      const result = await upload.upload(file)
      onUploaded(result)
    } catch {
      /**
       * useDocumentUpload already captured the error and set state.
       * row-level inline error display is out of scope.
       */
    }
  }

  const replaceLabel =
    upload.status === 'idle' ||
    upload.status === 'done' ||
    upload.status === 'error'
      ? 'Replace'
      : 'Uploading…'
  const replaceDisabled =
    upload.status === 'requesting' ||
    upload.status === 'uploading' ||
    upload.status === 'completing'

  const fileInput = (
    <input
      ref={inputRef}
      type='file'
      accept={CERT_ACCEPT}
      className='hidden'
      onChange={handleFileChange}
    />
  )

  if (variant === 'card') {
    return (
      <div className='flex flex-col gap-2 rounded-xl border border-border bg-page p-4'>
        <p className='text-[11px] font-medium tracking-wide text-ink-muted uppercase'>
          {SUPPLIER_CERTIFICATE_LABELS[cert]}
        </p>
        <button
          type='button'
          onClick={onView}
          className='cursor-pointer text-left text-sm font-medium break-words text-foreground hover:underline'
        >
          {fileName ?? '—'}
        </button>
        <p className='text-xs text-muted-foreground'>
          {formatContentType(contentType)} · {formatCreatedAt(createdAt)}
        </p>
        <div className='flex gap-2'>
          <button
            type='button'
            onClick={() => inputRef.current?.click()}
            disabled={replaceDisabled}
            className='inline-flex min-h-11 flex-1 cursor-pointer items-center justify-center rounded-md border border-border text-xs font-medium text-accent disabled:opacity-50 dark:text-accent-mid'
          >
            {replaceLabel}
          </button>
          {fileInput}
          <button
            type='button'
            onClick={onRemove}
            className='inline-flex min-h-11 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-border text-xs font-medium text-ink-muted hover:text-red-700 dark:hover:text-red-300'
          >
            <Trash2 className='size-3.5' />
            Remove
          </button>
        </div>
      </div>
    )
  }

  return (
    <TableRow>
      <TableCell className='text-[11px] font-medium tracking-wide text-ink-muted uppercase'>
        {SUPPLIER_CERTIFICATE_LABELS[cert]}
      </TableCell>
      <TableCell>
        <button
          type='button'
          onClick={onView}
          className='cursor-pointer truncate text-left text-sm font-medium text-foreground hover:underline'
        >
          {fileName ?? '—'}
        </button>
      </TableCell>
      <TableCell className='text-xs text-muted-foreground'>
        {formatContentType(contentType)}
      </TableCell>
      <TableCell className='text-xs text-muted-foreground'>
        {formatCreatedAt(createdAt)}
      </TableCell>
      <TableCell className='text-right'>
        <button
          type='button'
          onClick={() => inputRef.current?.click()}
          disabled={replaceDisabled}
          className='cursor-pointer text-xs font-medium text-accent dark:text-accent-mid hover:underline disabled:opacity-50'
        >
          {replaceLabel}
        </button>
        {fileInput}
      </TableCell>
      <TableCell className='text-right'>
        <button
          type='button'
          onClick={onRemove}
          aria-label='Remove certificate'
          className='inline-grid size-8 cursor-pointer place-items-center rounded-md text-ink-muted transition-colors hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40 dark:hover:text-red-300'
        >
          <Trash2 className='size-3.5' />
        </button>
      </TableCell>
    </TableRow>
  )
}

function formatContentType(contentType: string | null): string {
  if (!contentType) return '—'
  if (contentType === 'application/pdf') return 'PDF'
  if (contentType === 'image/png') return 'PNG'
  if (contentType === 'image/jpeg') return 'JPEG'
  return contentType
}

function formatCreatedAt(createdAt: string | null): string {
  if (!createdAt) return '—'
  const date = new Date(createdAt)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

function QaSkipBanner() {
  return (
    <div className='flex items-start gap-3 rounded-xl border border-accent/30 bg-accent-light/60 p-4'>
      <ShieldCheck className='size-4 shrink-0 text-accent dark:text-accent-mid' />
      <p className='text-[13px] text-ink'>
        Holding <strong>{QA_SKIP_CERTS_LABEL}</strong> auto-completes the
        supplier quality assessment step.
      </p>
    </div>
  )
}
